import { useState, useEffect } from "react";
import { createChildLogger } from "@retreat/shared";
import type { EditableRetreatSchedule } from "@retreat/core";
import { getTestSchedule } from "../utils/env";
// Telemetry is auto-initialized by importing @retreat/shared
import "@retreat/shared";
import { RetreatDisplay } from "./RetreatDisplay";
import { createVisibilityHandler } from "../lifecycle/bridgeHandlers";
import { RetreatState, RetreatManager } from "@retreats/engine";
import { oneDayRetreat } from "@retreats/content";
import { useRetreat } from "../hooks/useRetreat";
import { audioPlane, textPlane } from "../planes";
import { TEST_IDS } from "../testing/testIds";
import { setupDebugTools } from "../debug/setupDebugTools";

const log = createChildLogger("RetreatBridge");

const CONTENT_TO_BELL_TRADITION: Record<string, string> = {
  zen: 'soto_zen',
  vipassana: 'vipassana_goenka',
};

/**
 * RetreatBridge — Pure observer of the Engine.
 *
 * Session restore contract (PV-12, M0-P0-001, M0-P0-002):
 *   init() finds a session → sets pendingSession → shows blocking prompt.
 *   handleConfirmResume() → wall-clock offset → initialize → restoreAndResume.
 *   handleStartFresh()    → clearSession → initialize → IDLE.
 *   Engine never starts until the leader makes an explicit choice.
 *
 * Button contract (PV-8, PV-13, M0-P0-004):
 *   IDLE    → "Begin Retreat"
 *   RUNNING → "Complete the Retreat" (with confirmation dialog)
 *   No Pause, no Resume, no Stop.
 */
/**
 * Engine test harness — minimal two-button UI (Begin / Complete) with no auth.
 * Reachable at ?ui=<E2E_HARNESS_UI_KEY> (opaque token, see testIds.ts). Never shown to production users.
 *
 * TODO: retire this component once arch-zombie-session.spec.ts and
 * session-auto-complete.spec.ts are migrated to the /demo route. Those E2E
 * suites depend on the Begin/Complete flow here; migrating requires reworking
 * the test interactions to match the new RetreatBridge HomeScreen → live flow.
 */
export function LegacyRetreatBridge() {
  // Engine state from observer (Phase 2)
  const {
    status: engineState,
    elapsedSeconds,
    error,
  } = useRetreat();

  // PV-8: Complete the Retreat confirmation dialog
  const [showCompleteConfirmation, setShowCompleteConfirmation] =
    useState(false);

  // Attach lifecycle handlers
  useEffect(() => {
    const visibilityHandler = createVisibilityHandler(() =>
      RetreatManager.get(),
    );
    document.addEventListener("visibilitychange", visibilityHandler);
    return () => {
      document.removeEventListener("visibilitychange", visibilityHandler);
    };
  }, []);

  // Initialize the engine (singleton already exists from main.tsx)
  useEffect(() => {
    let active = true;

    const init = async () => {
      try {
        log.info("Initializing RetreatBridge");

        const manager = RetreatManager.get();
        window.__retreatManager = manager;

        // Debug utilities — setupDebugTools guards against double-wiring via window.__debugWired
        setupDebugTools();

        if (!active) return;

        // PV-12: check for an existing session before doing anything with the engine.
        // Allow test schedule injection in DEV mode or when __E2E_MODE__ is set by Playwright.
        const testSchedule = getTestSchedule<EditableRetreatSchedule>();
        const activeSchedule = testSchedule ?? oneDayRetreat;

        manager.selectRetreat(activeSchedule);
        await manager.probe();

        if (!active) return;

        // Auto-resume from wall-clock offset if a session was found.
        // Do NOT call audioPlane.resume() here — auto-resume is silent (no user gesture).
        // Chrome's AudioContext.resume() never resolves without a user gesture; calling it
        // here would cause the entire boot sequence to hang indefinitely.
        const pending = manager.getSnapshot().pendingSession;
        if (pending) {
          log.info("LegacyRetreatBridge: auto-resuming session from wall-clock offset");
          await manager.confirmResume(pending);
        }

        log.info("LegacyRetreatBridge initialized");
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        log.info("Error during initialization", { error: msg });
      }
    };

    init();
    return () => {
      active = false;
    };
  }, []);

  // User clicks "Begin Retreat" from IDLE state.
  const handleStartRetreat = async () => {
    try {
      log.info("Begin Retreat clicked", {
        className: "RetreatBridge",
        methodName: "handleStartRetreat",
        action: "User requested retreat start",
      });

      const manager = RetreatManager.get();

      if (!audioPlane) {
        throw new Error("Audio plane not initialized");
      }

      const currentState = manager.getState();
      if (currentState === RetreatState.RUNNING) {
        log.info("Manager already running, ignoring start request");
        return;
      }

      // After a stop cycle the engine returns to IDLE with all managers shutdown
      // and activeSession nulled. Re-run the init flow (mirrors the mount effect)
      // so initialize() can transition IDLE → INITIALIZING before start() fires.
      const testSchedule = getTestSchedule<EditableRetreatSchedule>();
      const activeSchedule = testSchedule ?? oneDayRetreat;

      // iOS gesture policy: AudioContext.resume() must be called before any real async
      // operation (IndexedDB, timers). manager.probe() crosses task boundaries and loses
      // the gesture context — audio unlock must happen first.
      if (audioPlane.resume && typeof audioPlane.resume === "function") {
        log.info("Audio context resume", {
          className: "RetreatBridge",
          methodName: "handleStartRetreat",
          action: "Resuming audio context",
        });
        await audioPlane.resume();
        const rawTradition = (activeSchedule.metadata?.tradition ?? "").toLowerCase();
        const traditionId = CONTENT_TO_BELL_TRADITION[rawTradition] ?? rawTradition;
        audioPlane.setTradition(traditionId);
      }

      if (currentState === RetreatState.IDLE) {
        manager.selectRetreat(activeSchedule);
        await manager.probe();
      }

      log.info("Manager start() called", {
        className: "RetreatBridge",
        methodName: "handleStartRetreat",
        action: "Starting retreat manager",
        state: currentState,
      });
      await manager.start();

      log.info("Retreat execution started", {
        className: "RetreatBridge",
        methodName: "handleStartRetreat",
        action: "Retreat started",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      log.info("Error starting retreat", { error: msg });
    }
  };

  // PV-8: user taps "Complete the Retreat" — shows inline confirmation.
  const handleCompleteRetreat = () => {
    setShowCompleteConfirmation(true);
  };

  // PV-8: user confirms early completion.
  const handleCompleteConfirm = async () => {
    setShowCompleteConfirmation(false);
    const manager = RetreatManager.get();

    // P0-029: timer will reset to 0:00 when stop() completes and transitions to IDLE
    // (snapshot update via _transitionTo)

    try {
      log.info("Stopping engine and clearing session", {
        className: "RetreatBridge",
        methodName: "handleCompleteConfirm",
        action: "User confirmed retreat completion",
      });

      // stop() clears the session internally — no separate coordinator.clearSession() needed.
      await manager.stop();
      audioPlane.clearTradition();

      log.info("Retreat completed — session cleared");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      log.info("Error during completion", { error: msg });
    }
  };

  // PV-8: user cancels — retreat continues uninterrupted.
  const handleCompleteCancel = () => {
    setShowCompleteConfirmation(false);
  };

  // Visibility handling is delegated to the lifecycle module via bridgeHandlers

  const getStatusMessage = (state: string | null): string => {
    if (!state || state === RetreatState.IDLE)
      return "Ready - click Begin Retreat to start";
    if (state === RetreatState.INITIALIZING) return "Initializing...";
    if (state === RetreatState.RUNNING) return "Retreat in progress...";
    return "Unknown state";
  };

  // PV-8, PV-13: no pause/resume in v1
  const shouldShowBeginButton =
    engineState === RetreatState.IDLE ||
    engineState === RetreatState.INITIALIZING ||
    !engineState;
  const shouldShowCompleteButton = engineState === RetreatState.RUNNING;

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-6">
      {error ? (
        <div className="bg-red-100 border-2 border-red-500 text-red-800 p-6 rounded-lg max-w-md">
          <h2 className="font-bold text-lg mb-2">Error</h2>
          <p className="text-sm">{error}</p>
          <p className="text-xs mt-4 text-gray-600">
            Check browser console for details
          </p>
        </div>
      ) : (
        <>
          <div className="text-white text-center">
            <h1 className="text-4xl font-bold mb-4">Retreat Conductor</h1>
            <p className="text-2xl mb-2">{getStatusMessage(engineState)}</p>
          </div>

          <div className="flex gap-4">
            {shouldShowBeginButton && (
              <button
                onClick={handleStartRetreat}
                data-testid={TEST_IDS.BEGIN_RETREAT_BTN}
                className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-full font-bold text-lg animate-pulse transition-colors"
              >
                Begin Retreat
              </button>
            )}
            {shouldShowCompleteButton && (
              <button
                onClick={handleCompleteRetreat}
                data-testid={TEST_IDS.COMPLETE_RETREAT_BTN}
                className="px-6 py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-full font-bold text-lg transition-colors"
              >
                Complete the Retreat
              </button>
            )}
          </div>

          <RetreatDisplay
            textPlane={textPlane ?? undefined}
            elapsedSeconds={elapsedSeconds}
          />
        </>
      )}

      {/* PV-8: Complete the Retreat confirmation overlay */}
      {showCompleteConfirmation && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 text-center max-w-sm">
            <p className="text-white text-xl font-bold mb-6">
              End this retreat early?
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={handleCompleteConfirm}
                data-testid={TEST_IDS.CONFIRM_COMPLETE_BTN}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={handleCompleteCancel}
                data-testid={TEST_IDS.CANCEL_COMPLETE_BTN}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-full font-bold transition-colors"
              >
                No, continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
